import '~/utils/webviewPolyfills';
import { ClerkProvider, useAuth, useClerk } from '@clerk/nextjs';
import { AssistantEnabledProvider } from '~/components/assistant-ui/AssistantEnabledContext';
import { GlazierAssistantProvider } from '~/components/assistant-ui/GlazierAssistantProvider';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { trpc } from '@utils/trpc';
import { registerClerkForSessionRecovery } from '@utils/clerkSessionRecovery';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { NextWebVitalsMetric } from 'next/dist/shared/lib/utils';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ErrorBoundary from '~/components/error-handling/ErrorBoundary';
import { Toaster } from '~/components/utils/Toaster';
import { DefaultLayout } from '~/layout/DefaultLayout';

//to remove default margins and paddings
import type { Role } from '@prisma/client';
import type { PermissionKey } from '@schema/permission.schema';
import type { FeatureFlag } from '@utils/featureFlags';
import { isNullish } from '@utils/types';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import z from 'zod';
import '../../styles/globals.css';
import { NavPermissionChecker } from '../components/navigation/navAuthChecker';

z.config({
	customError: (iss) => {
		// Default error messages for some common use cases that are missing a custom error on the schema itself
		if (iss.code === 'invalid_type' || iss.code === 'invalid_value') {
			if (isNullish(iss.input)) {
				return 'Required';
			}
			if (Number.isNaN(iss.input)) {
				return 'Enter a valid number';
			}
		}
		if (iss.code === 'too_big') {
			switch (iss.origin) {
				case 'number':
					return `Number must be less than ${iss.maximum}`;
				case 'array':
					return `Too many selections`;
				default:
					return (iss.error || iss.message) as string | null;
			}
		}
		if (iss.code === 'too_small') {
			switch (iss.origin) {
				case 'number':
					return `Number must be greater than ${iss.minimum}`;
				case 'array':
				case 'string':
					// Assuming min(1). Custom errors should be added to that form's schema
					return `Required`;
				default:
					return (iss.error || iss.message) as string | null;
			}
		}
		return (iss.error || iss.message) as string | null;
	},
});

datadogLogs.init({
	beforeSend: (event) => {
		if (event.message.indexOf('A component is changing from uncontrolled to controlled.') !== -1) {
			return false;
		}
		return true;
	},
	clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN!,
	forwardErrorsToLogs: true,
	silentMultipleInit: true,
	site: process.env.NEXT_PUBLIC_DATADOG_SITE,
});
if (process.env.NEXT_PUBLIC_RUM_ENABLED === 'true') {
	datadogRum.init({
		applicationId: process.env.NEXT_PUBLIC_RUM_DATADOG_APPLICATION_ID!,
		clientToken: process.env.NEXT_PUBLIC_RUM_DATADOG_CLIENT_TOKEN!,
		site: process.env.NEXT_PUBLIC_DATADOG_SITE!,
		service: process.env.NEXT_PUBLIC_RUM_DATADOG_SERVICE!,
		sessionReplaySampleRate: 100,
		trackResources: true,
		trackLongTasks: true,
		trackUserInteractions: true,
		defaultPrivacyLevel: 'allow',
		silentMultipleInit: true,
	});

	datadogRum.startSessionReplayRecording();
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
	datadogLogs.logger.info('next web vitals', metric);
}

export type NextPageWithLayout<TProps = Record<string, unknown>, TInitialProps = TProps> = NextPage<TProps, TInitialProps> & {
	getLayout?: (page: ReactElement, props: TProps) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
	Component: NextPageWithLayout;
};

type UserWithPermissionKeys = {
	allowedPermissions: PermissionKey[];
	businessId: number;
	businessName: string;
	email: string | null;
	id: number;
	role: Role;
	firstName: string;
	lastName: string;
	userPermission: {
		permission: {
			key: string;
		};
	}[];
};

/**
 * Routes that render without a Clerk session.
 *
 * These are reached by people who are not Glazier users — customers opening a quote, payment, or
 * review link, and the mobile app's webviews (which authenticate with the MOBILE_APP_KEY URL segment,
 * not Clerk). They must not mount `ClerkProvider`: loading Clerk on a third-party-facing page would
 * be a behavior change, and `CustomerPageLayout` has deliberately never had a provider.
 *
 * Deliberately NOT listed here: `/signed-out`, which has no session but does call `useAuth`, so it
 * needs the provider. This is why the list is not the same as the auth-check skip list below.
 */
const isClerkFreeRoute = (pathname: string) => pathname.startsWith('/customer-pages') || pathname.startsWith('/embed');

/**
 * Supplies the assistant with Clerk's session state.
 *
 * Must be a separate component rendered *inside* `ClerkProvider`: `useAuth` throws when no provider
 * is above it, and on Clerk-free routes we render the assistant tree without one. Those routes get
 * `hasSession={false}` from the default below instead.
 */
const AssistantEnabledProviderWithSession = ({ children }: { children: ReactNode }) => {
	const { isLoaded, isSignedIn } = useAuth();

	return (
		<AssistantEnabledProvider hasSession={isLoaded && !!isSignedIn} sessionResolved={isLoaded}>
			{children}
		</AssistantEnabledProvider>
	);
};

const ClerkSessionRecoveryRegistration = () => {
	const clerk = useClerk();

	useEffect(() => {
		registerClerkForSessionRecovery(clerk);

		return () => registerClerkForSessionRecovery(null);
	}, [clerk]);

	return null;
};

const GlazierApp = ({ Component, pageProps }: AppPropsWithLayout) => {
	const getLayout = Component.getLayout ?? ((page) => <DefaultLayout featureFlags={[] as FeatureFlag[]}>{page}</DefaultLayout>);

	const [authChecked, setAuthChecked] = useState(false);
	const pathname = usePathname();
	const [user, setUser] = useState<UserWithPermissionKeys | null>(null);
	const [permissions, setPermissions] = useState<PermissionKey[]>([]);
	const router = useRouter();
	// Get the navigation configuration

	useEffect(() => {
		if (
			pathname === '/404' ||
			pathname === '/500' ||
			pathname === '/sign-in' ||
			pathname === '/login' ||
			pathname === '/signed-out' ||
			pathname === '/403' ||
			// don't need to check auth on customer facing pages.
			pathname.indexOf('/customer-pages') > -1 ||
			// don't need to check auth on embed pages.
			pathname.indexOf('/embed') > -1
		) {
			setAuthChecked(true);
			return;
		}
		// Call the authorization API to fetch user data
		const fetchUserPermissions = async () => {
			try {
				const res = await fetch('/api/auth', {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				});

				if (res.status === 401) {
					// If unauthorized, redirect to login
					void router.push('/sign-in');
					return;
				}

				const data = (await res.json()) as UserWithPermissionKeys;
				setUser(data);
				setPermissions(data.allowedPermissions);

				// Create the permission checker
				const permissionChecker = new NavPermissionChecker(data.allowedPermissions);

				const authorizationResult = permissionChecker.isAuthorized(pathname);
				if (!authorizationResult.authorized) {
					// If unauthorized, redirect to 403 with some nice details on permission check failures.
					void router.push(
						`/403?userId=${data.id}&userName=${data.firstName} ${data.lastName}& path=${authorizationResult.path}&permissionKey=${authorizationResult.permissionKey}&fullUrl=${router.asPath}`
					);
					return;
				}

				// NB: Don't use `user` here as it won't have a value until the next render
				if (data !== null && process.env.NEXT_PUBLIC_RUM_ENABLED === 'true') {
					// @ts-ignore pendo is loaded via _document.tsx
					pendo.initialize({
						visitor: {
							id: data.id,
							email: data.email,
							full_name: `${data.firstName} ${data.lastName}`,
							role: data.role,
							// You can add any additional visitor level key-values here,
							// as long as it's not one of the above reserved names.
						},
						account: {
							id: data.businessId,
							name: data.businessName,
							// is_paying:    // Recommended if using Pendo Feedback
							// monthly_value:// Recommended if using Pendo Feedback
							// planLevel:    // Optional
							// planPrice:    // Optional
							// creationDate: // Optional
							// You can add any additional account level key-values here,
							// as long as it's not one of the above reserved names.
						},
					});
				}

				setAuthChecked(true);
			} catch (error) {
				console.error('Error fetching user permissions:', error);
			}
		};

		void fetchUserPermissions();
	}, [pathname, router]);

	// If authentication hasn't been checked yet, you can return a loading state or null
	const assistantTree = (
		<DndProvider backend={HTML5Backend}>
			<GlazierAssistantProvider>
				{getLayout(
					<ErrorBoundary>
						<Component {...pageProps} user={user} permissions={permissions} />
						<Toaster />
					</ErrorBoundary>,
					pageProps
				)}
			</GlazierAssistantProvider>
		</DndProvider>
	);

	const contents = !authChecked ? (
		<div>Auth Check...</div>
	) : isClerkFreeRoute(pathname) ? (
		<AssistantEnabledProvider>{assistantTree}</AssistantEnabledProvider>
	) : (
		<AssistantEnabledProviderWithSession>{assistantTree}</AssistantEnabledProviderWithSession>
	);

	if (isClerkFreeRoute(pathname)) {
		return contents;
	}

	// Pass the publishable key explicitly. Clerk v4's ClerkProvider takes it as a prop and falls back
	// to a key baked into the package at build time — Clerk's own staging instance, not ours — so a
	// propless provider silently connects to the wrong Clerk domain rather than failing. The v4-only
	// NEXT_PUBLIC_CLERK_FRONTEND_API happens to resolve correctly today, but it is removed in v5, and
	// relying on it is what masked this.
	return (
		<ClerkProvider publishableKey={process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}>
			<ClerkSessionRecoveryRegistration />
			{contents}
		</ClerkProvider>
	);
};

export default trpc.withTRPC(GlazierApp);
